<template>
    <v-card outlined class="filter-panel mb-4">
        <div class="filter-header" @click="expanded = !expanded">
            <v-icon small class="mr-2">mdi-filter-variant</v-icon>
            <span class="section-title">Filters</span>
            <v-chip v-if="activeCount" x-small color="secondary" class="ml-2" dark>{{ activeCount }} active</v-chip>
            <v-spacer />
            <v-btn v-if="activeCount" text small color="primary" class="mr-1" @click.stop="reset">
                Clear all
            </v-btn>
            <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
            <div v-show="expanded">
                <v-divider />
                <v-form class="pa-4 pb-2" @submit.prevent="apply">
                    <v-row dense>
                        <v-col
                            v-for="field in fields"
                            :key="field.key"
                            cols="12"
                            sm="6"
                            md="4"
                            lg="3"
                        >
                            <date-field
                                v-if="field.type === 'date'"
                                v-model="local[field.key]"
                                :label="field.label"
                                dense
                            />
                            <v-autocomplete
                                v-else-if="field.type === 'autocomplete' || field.type === 'select'"
                                v-model="local[field.key]"
                                :items="field.items || []"
                                :item-text="field.itemText || 'text'"
                                :item-value="field.itemValue || 'id'"
                                :label="field.label"
                                :loading="!field.items"
                                outlined
                                dense
                                clearable
                                hide-details
                            />
                            <v-select
                                v-else-if="field.type === 'boolean'"
                                v-model="local[field.key]"
                                :items="booleanItems"
                                :label="field.label"
                                outlined
                                dense
                                clearable
                                hide-details
                            />
                            <v-text-field
                                v-else
                                v-model.trim="local[field.key]"
                                :type="field.type === 'number' ? 'number' : 'text'"
                                :label="field.label"
                                :placeholder="field.placeholder"
                                outlined
                                dense
                                clearable
                                hide-details
                            />
                        </v-col>
                    </v-row>
                    <div class="filter-actions">
                        <v-btn text @click="reset">Reset</v-btn>
                        <v-btn color="primary" depressed type="submit" :loading="loading">
                            <v-icon left>mdi-magnify</v-icon>Apply
                        </v-btn>
                    </div>
                </v-form>
            </div>
        </v-expand-transition>
    </v-card>
</template>

<script>
    import DateField from '@/components/DateField'

    // Emits `apply` with { filters }; empty values are dropped. Sorting is done with the table's column headers.
    export default {
        name: 'FilterPanel',
        components: { DateField },
        props: {
            fields: {
                type: Array,
                required: true
            },
            value: {
                type: Object,
                default: () => ({ filters: {}, sortOption: null, order: 'ASC' })
            },
            loading: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                local: {},
                expanded: true,
                booleanItems: [
                    { text: 'Yes', value: true },
                    { text: 'No', value: false }
                ]
            }
        },
        computed: {
            activeCount() {
                return Object.keys(this.clean(this.value.filters || {})).length
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(value) {
                    const known = this.fields.map(field => field.key)
                    const filters = {}
                    Object.keys(value.filters || {}).filter(key => known.includes(key)).forEach(key => {
                        filters[key] = value.filters[key]
                    })
                    this.local = filters
                }
            }
        },
        created() {
            this.expanded = this.$vuetify.breakpoint.mdAndUp || this.activeCount > 0
        },
        methods: {
            clean(filters) {
                const result = {}
                Object.keys(filters).forEach(key => {
                    const value = filters[key]
                    if (value !== null && value !== undefined && value !== '') {
                        result[key] = value
                    }
                })
                return result
            },
            apply() {
                this.$emit('apply', { filters: this.clean(this.local) })
            },
            reset() {
                this.local = {}
                this.apply()
            }
        }
    }
</script>

<style scoped>
.filter-header {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
}

.filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 0 4px;
}
</style>
